import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FFmpeg, LogEventCallback, ProgressEventCallback } from '@ffmpeg/ffmpeg';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  InputNumber,
  Progress,
  Row,
  Select,
  Slider,
  Space,
  Statistic,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  CloudDownloadOutlined,
  CompressOutlined,
  InboxOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SettingOutlined,
  StopOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import ToolPageHeader from '../../components/ToolPageHeader';
import { useTranslation } from '../../hooks/useTranslation';
import { getImgCloudBasename } from '../../utils/appRouting';
import './index.css';

const { Dragger } = Upload;
const { Text, Title } = Typography;

type CompressionStatus = 'idle' | 'ready' | 'loading' | 'compressing' | 'completed' | 'error';
type OutputFormat = 'mp4' | 'webm';
type ProfilePreset = 'fast' | 'balanced' | 'high' | 'custom';
type EncoderPreset = 'ultrafast' | 'superfast' | 'veryfast' | 'faster' | 'fast';
type ResolutionHeight = 'source' | 'custom' | 360 | 480 | 720 | 1080;

interface VideoMetadata {
  duration: number;
  width: number;
  height: number;
}

interface TargetDimensions {
  width: number;
  height: number;
}

interface CompressionSettings {
  profile: ProfilePreset;
  format: OutputFormat;
  resolutionHeight: ResolutionHeight;
  customHeight: number;
  fps: number;
  bitrateMbps: number;
  crf: number;
  audioBitrateKbps: number;
  encoderPreset: EncoderPreset;
}

interface ProbeResult {
  rotation: number;
}

interface FfmpegRuntime {
  ffmpeg: FFmpeg;
  fetchFile: (file?: string | File | Blob) => Promise<Uint8Array>;
}

interface FfmpegCoreUrls {
  coreURL: string;
  wasmURL: string;
}

const MAX_INPUT_BYTES = 512 * 1024 * 1024;
const LARGE_FILE_WARNING_BYTES = 256 * 1024 * 1024;
const SUPPORTED_EXTENSIONS = ['mp4', 'mov', 'webm', 'avi', 'mkv', 'm4v'];
const FORMAT_MIME: Record<OutputFormat, string> = {
  mp4: 'video/mp4',
  webm: 'video/webm',
};

const DEFAULT_SETTINGS: CompressionSettings = {
  profile: 'balanced',
  format: 'mp4',
  resolutionHeight: 720,
  customHeight: 720,
  fps: 30,
  bitrateMbps: 1.2,
  crf: 26,
  audioBitrateKbps: 128,
  encoderPreset: 'veryfast',
};

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

const even = (value: number): number => Math.max(2, Math.floor(value / 2) * 2);

const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / Math.pow(1024, index);
  return `${value.toFixed(value >= 10 || index === 0 ? 0 : 1)} ${units[index]}`;
};

const formatDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return '--';
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60).toString().padStart(2, '0');
  return `${mins}:${secs}`;
};

const getFileExtension = (fileName: string): string => {
  const match = fileName.toLowerCase().match(/\.([a-z0-9]+)$/);
  return match ? match[1] : '';
};

const getBaseFileName = (fileName: string): string => {
  const baseName = fileName.replace(/\.[^.]+$/, '').trim();
  return baseName || 'compressed-video';
};

const triggerBlobDownload = (url: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const isSupportedVideo = (file: File): boolean => {
  const extension = getFileExtension(file.name);
  return file.type.startsWith('video/') || SUPPORTED_EXTENSIONS.includes(extension);
};

const isBrowserSupported = (): boolean => (
  typeof WebAssembly !== 'undefined' &&
  typeof Worker !== 'undefined' &&
  typeof Blob !== 'undefined' &&
  typeof URL !== 'undefined'
);

const computeTargetDimensions = (
  metadata: VideoMetadata | null,
  settings: Pick<CompressionSettings, 'resolutionHeight' | 'customHeight'>,
): TargetDimensions => {
  const sourceWidth = metadata?.width || 1280;
  const sourceHeight = metadata?.height || 720;
  const requestedHeight = settings.resolutionHeight === 'source'
    ? sourceHeight
    : settings.resolutionHeight === 'custom'
      ? settings.customHeight
      : settings.resolutionHeight;
  const targetHeight = clamp(Math.round(requestedHeight), 120, sourceHeight);
  const targetWidth = targetHeight * (sourceWidth / sourceHeight);
  return {
    width: even(targetWidth),
    height: even(targetHeight),
  };
};

const estimateBitrateMbps = (
  file: File | null,
  metadata: VideoMetadata | null,
  dimensions: TargetDimensions,
  fps: number,
  profile: ProfilePreset,
): number => {
  const duration = metadata?.duration || 0;
  const sourceWidth = metadata?.width || dimensions.width;
  const sourceHeight = metadata?.height || dimensions.height;
  const sourceMbps = file && duration > 0 ? (file.size * 8) / duration / 1_000_000 : 3;
  const pixelRatio = sourceWidth > 0 && sourceHeight > 0
    ? (dimensions.width * dimensions.height) / (sourceWidth * sourceHeight)
    : 1;
  const fpsRatio = fps / 30;
  const profileRatio = profile === 'fast' ? 0.42 : profile === 'high' ? 0.72 : 0.55;
  const target = sourceMbps * pixelRatio * fpsRatio * profileRatio;
  const upperBound = Math.max(0.8, sourceMbps * 0.85);
  return Number(clamp(target, 0.25, Math.min(30, upperBound)).toFixed(2));
};

const buildPresetSettings = (
  profile: ProfilePreset,
  current: CompressionSettings,
  metadata: VideoMetadata | null,
  file: File | null,
): CompressionSettings => {
  const presetBase: Record<Exclude<ProfilePreset, 'custom'>, Partial<CompressionSettings>> = {
    fast: {
      resolutionHeight: 480,
      fps: 24,
      crf: 30,
      audioBitrateKbps: 96,
      encoderPreset: 'superfast',
    },
    balanced: {
      resolutionHeight: 720,
      fps: 30,
      crf: 26,
      audioBitrateKbps: 128,
      encoderPreset: 'veryfast',
    },
    high: {
      resolutionHeight: 'source',
      fps: 30,
      crf: 23,
      audioBitrateKbps: 160,
      encoderPreset: 'faster',
    },
  };

  if (profile === 'custom') {
    return { ...current, profile };
  }

  const next = { ...current, ...presetBase[profile], profile } as CompressionSettings;
  const dimensions = computeTargetDimensions(metadata, next);
  return {
    ...next,
    bitrateMbps: estimateBitrateMbps(file, metadata, dimensions, next.fps, profile),
  };
};

const buildFfmpegCoreCandidates = (): string[] => {
  const candidates = new Set<string>();
  const publicUrl = process.env.PUBLIC_URL ? process.env.PUBLIC_URL.replace(/\/$/, '') : '';
  const basename = getImgCloudBasename();

  if (publicUrl) {
    candidates.add(`${publicUrl}/ffmpeg-core`);
  }
  if (basename) {
    candidates.add(`${basename}/ffmpeg-core`);
  }
  candidates.add('/img-cloud/ffmpeg-core');
  candidates.add('/ffmpeg-core');

  return Array.from(candidates);
};

const isAvailableAsset = async (url: string): Promise<boolean> => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    const contentType = response.headers.get('content-type') || '';
    return response.ok && !contentType.includes('text/html');
  } catch (error) {
    return false;
  }
};

const resolveFfmpegCoreUrls = async (): Promise<FfmpegCoreUrls> => {
  const candidates = buildFfmpegCoreCandidates();
  let firstAvailableCore = '';
  let firstAvailableWasm = '';

  for (const candidate of candidates) {
    const coreURL = `${candidate}/ffmpeg-core.js`;
    const wasmURL = `${candidate}/ffmpeg-core.wasm`;
    const [coreAvailable, wasmAvailable] = await Promise.all([
      isAvailableAsset(coreURL),
      isAvailableAsset(wasmURL),
    ]);

    if (coreAvailable && !firstAvailableCore) {
      firstAvailableCore = coreURL;
    }
    if (wasmAvailable && !firstAvailableWasm) {
      firstAvailableWasm = wasmURL;
    }
    if (coreAvailable && wasmAvailable) {
      return { coreURL, wasmURL };
    }
  }

  // Some deployments mount JS and WASM under different prefixes; FFmpeg accepts separate URLs.
  return {
    coreURL: firstAvailableCore || `${candidates[candidates.length - 1]}/ffmpeg-core.js`,
    wasmURL: firstAvailableWasm || `${candidates[candidates.length - 1]}/ffmpeg-core.wasm`,
  };
};

const buildVideoFilter = (dimensions: TargetDimensions, fps: number, rotation: number): string => {
  const normalizedRotation = ((Math.round(rotation) % 360) + 360) % 360;
  const rotateFilter = normalizedRotation === 90
    ? 'transpose=1,'
    : normalizedRotation === 270
      ? 'transpose=2,'
      : normalizedRotation === 180
        ? 'transpose=1,transpose=1,'
        : '';
  return `${rotateFilter}scale=${dimensions.width}:${dimensions.height}:flags=fast_bilinear,fps=${fps}`;
};

const parseProbeRotation = (logText: string): number => {
  const rotateTag = logText.match(/rotate\s*:\s*(-?\d+)/i);
  const sideDataRotation = logText.match(/rotation of\s*(-?\d+(?:\.\d+)?)\s*degrees/i);
  const rotation = rotateTag ? Number(rotateTag[1]) : sideDataRotation ? Number(sideDataRotation[1]) : 0;
  const normalizedRotation = ((Math.round(rotation) % 360) + 360) % 360;
  return [90, 180, 270].includes(normalizedRotation) ? normalizedRotation : 0;
};

const probeInput = async (ffmpeg: FFmpeg, inputName: string): Promise<ProbeResult> => {
  const logs: string[] = [];
  const logHandler: LogEventCallback = ({ message: logMessage }) => {
    logs.push(logMessage);
  };

  ffmpeg.on('log', logHandler);
  try {
    await ffmpeg.exec(['-hide_banner', '-i', inputName]);
  } catch (error) {
    // ffmpeg returns a non-zero code for metadata-only probes; logs still contain stream info.
  } finally {
    ffmpeg.off('log', logHandler);
  }

  return { rotation: parseProbeRotation(logs.join('\n')) };
};

const buildCompressionArgs = (
  inputName: string,
  outputName: string,
  settings: CompressionSettings,
  dimensions: TargetDimensions,
  probe: ProbeResult,
): string[] => {
  const bitrateKbps = Math.max(100, Math.round(settings.bitrateMbps * 1000));
  const filter = buildVideoFilter(dimensions, settings.fps, probe.rotation);
  const inputArgs = ['-i', inputName, '-map', '0:v:0', '-map', '0:a?'];

  if (settings.format === 'webm') {
    return [
      ...inputArgs,
      '-c:v', 'libvpx-vp9',
      '-b:v', `${bitrateKbps}k`,
      '-crf', String(clamp(settings.crf - 8, 12, 42)),
      '-deadline', 'realtime',
      '-cpu-used', settings.encoderPreset === 'ultrafast' || settings.encoderPreset === 'superfast' ? '8' : '6',
      '-vf', filter,
      '-metadata:s:v:0', 'rotate=0',
      '-c:a', 'libopus',
      '-b:a', `${settings.audioBitrateKbps}k`,
      '-y',
      outputName,
    ];
  }

  return [
    ...inputArgs,
    '-c:v', 'libx264',
    '-b:v', `${bitrateKbps}k`,
    '-maxrate', `${Math.round(bitrateKbps * 1.5)}k`,
    '-bufsize', `${Math.round(bitrateKbps * 2)}k`,
    '-vf', filter,
    '-metadata:s:v:0', 'rotate=0',
    '-crf', String(settings.crf),
    '-preset', settings.encoderPreset,
    '-pix_fmt', 'yuv420p',
    '-c:a', 'aac',
    '-b:a', `${settings.audioBitrateKbps}k`,
    '-movflags', '+faststart',
    '-y',
    outputName,
  ];
};

const VideoCompressor: React.FC = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<CompressionStatus>('idle');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [metadata, setMetadata] = useState<VideoMetadata | null>(null);
  const [settings, setSettings] = useState<CompressionSettings>(DEFAULT_SETTINGS);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [errorText, setErrorText] = useState('');
  const [resultBlob, setResultBlob] = useState<Blob | null>(null);
  const [resultUrl, setResultUrl] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const fetchFileRef = useRef<FfmpegRuntime['fetchFile'] | null>(null);
  const startedAtRef = useRef(0);
  const cancelRequestedRef = useRef(false);

  const targetDimensions = useMemo(
    () => computeTargetDimensions(metadata, {
      resolutionHeight: settings.resolutionHeight,
      customHeight: settings.customHeight,
    }),
    [metadata, settings.resolutionHeight, settings.customHeight],
  );

  const estimatedOutputBytes = useMemo(() => {
    const duration = metadata?.duration || 0;
    if (duration <= 0) {
      return 0;
    }
    return Math.round((duration * (settings.bitrateMbps * 1000 + settings.audioBitrateKbps) * 1000) / 8);
  }, [metadata?.duration, settings.audioBitrateKbps, settings.bitrateMbps]);

  const compressionRate = useMemo(() => {
    if (!videoFile || !resultBlob) {
      return 0;
    }
    return Math.round((1 - resultBlob.size / videoFile.size) * 1000) / 10;
  }, [resultBlob, videoFile]);

  useEffect(() => () => {
    if (ffmpegRef.current) {
      ffmpegRef.current.terminate();
      ffmpegRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  }, [previewUrl]);

  useEffect(() => () => {
    if (resultUrl) {
      URL.revokeObjectURL(resultUrl);
    }
  }, [resultUrl]);

  useEffect(() => {
    if (status !== 'compressing') {
      return undefined;
    }
    const timer = window.setInterval(() => {
      if (startedAtRef.current) {
        setElapsedSeconds(Math.round((Date.now() - startedAtRef.current) / 1000));
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [status]);

  const updateSettings = useCallback((patch: Partial<CompressionSettings>) => {
    setSettings((prev) => ({ ...prev, ...patch, profile: patch.profile || 'custom' }));
  }, []);

  const applyProfile = useCallback((profile: ProfilePreset) => {
    setSettings((prev) => buildPresetSettings(profile, prev, metadata, videoFile));
  }, [metadata, videoFile]);

  const clearResult = useCallback(() => {
    setResultBlob(null);
    setResultUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return '';
    });
    setProgress(0);
    setProgressText('');
    setElapsedSeconds(0);
  }, []);

  const handleFileSelect = useCallback((file: File) => {
    if (!isSupportedVideo(file)) {
      message.error(t('imageProcessing.tools.videoCompressor.unsupportedFormat'));
      return;
    }
    if (file.size > MAX_INPUT_BYTES) {
      message.error(t('imageProcessing.tools.videoCompressor.fileTooLarge'));
      return;
    }
    if (file.size > LARGE_FILE_WARNING_BYTES) {
      message.warning(t('imageProcessing.tools.videoCompressor.largeFileWarning'));
    }

    const url = URL.createObjectURL(file);
    setVideoFile(file);
    setPreviewUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return url;
    });
    setMetadata(null);
    setSettings(DEFAULT_SETTINGS);
    clearResult();
    setErrorText('');
    setStatus('ready');
  }, [clearResult, t]);

  const handleMetadataLoaded = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = event.currentTarget;
    const nextMetadata = {
      duration: Number.isFinite(video.duration) ? video.duration : 0,
      width: video.videoWidth || 0,
      height: video.videoHeight || 0,
    };
    setMetadata(nextMetadata);
    setSettings((prev) => buildPresetSettings('balanced', { ...prev, profile: 'balanced' }, nextMetadata, videoFile));
  }, [videoFile]);

  const ensureFfmpeg = useCallback(async (): Promise<FfmpegRuntime> => {
    if (ffmpegRef.current && fetchFileRef.current) {
      return { ffmpeg: ffmpegRef.current, fetchFile: fetchFileRef.current };
    }

    const [{ FFmpeg: FFmpegCtor }, { fetchFile, toBlobURL }] = await Promise.all([
      import('@ffmpeg/ffmpeg'),
      import('@ffmpeg/util'),
    ]);
    const ffmpeg = new FFmpegCtor();
    const coreAssets = await resolveFfmpegCoreUrls();
    const [coreURL, wasmURL] = await Promise.all([
      toBlobURL(coreAssets.coreURL, 'text/javascript'),
      toBlobURL(coreAssets.wasmURL, 'application/wasm'),
    ]);

    await ffmpeg.load({ coreURL, wasmURL });
    ffmpegRef.current = ffmpeg;
    fetchFileRef.current = fetchFile;
    return { ffmpeg, fetchFile };
  }, []);

  const validateBeforeCompress = useCallback((): boolean => {
    if (!isBrowserSupported()) {
      setErrorText(t('imageProcessing.tools.videoCompressor.browserUnsupported'));
      setStatus('error');
      return false;
    }
    if (!videoFile) {
      message.warning(t('imageProcessing.tools.videoCompressor.selectVideoFirst'));
      return false;
    }
    if (!isSupportedVideo(videoFile)) {
      setErrorText(t('imageProcessing.tools.videoCompressor.unsupportedFormat'));
      setStatus('error');
      return false;
    }
    if (videoFile.size > MAX_INPUT_BYTES) {
      setErrorText(t('imageProcessing.tools.videoCompressor.fileTooLarge'));
      setStatus('error');
      return false;
    }
    if (settings.bitrateMbps < 0.1 || settings.bitrateMbps > 50) {
      setErrorText(t('imageProcessing.tools.videoCompressor.invalidBitrate'));
      setStatus('error');
      return false;
    }
    if (settings.crf < 18 || settings.crf > 40) {
      setErrorText(t('imageProcessing.tools.videoCompressor.invalidCrf'));
      setStatus('error');
      return false;
    }
    if (settings.fps < 10 || settings.fps > 60) {
      setErrorText(t('imageProcessing.tools.videoCompressor.invalidFps'));
      setStatus('error');
      return false;
    }
    return true;
  }, [settings.bitrateMbps, settings.crf, settings.fps, t, videoFile]);

  const startCompression = useCallback(async () => {
    if (!validateBeforeCompress() || !videoFile) {
      return;
    }

    cancelRequestedRef.current = false;
    startedAtRef.current = Date.now();
    setStatus('loading');
    setErrorText('');
    clearResult();

    const inputExtension = getFileExtension(videoFile.name) || 'mp4';
    const inputName = `input.${inputExtension}`;
    const outputName = `output.${settings.format}`;
    const outputType = FORMAT_MIME[settings.format];
    let ffmpeg: FFmpeg | null = null;

    try {
      setProgressText(t('imageProcessing.tools.videoCompressor.loadingEngine'));
      const runtime = await ensureFfmpeg();
      ffmpeg = runtime.ffmpeg;
      setStatus('compressing');
      setProgress(1);
      setProgressText(t('imageProcessing.tools.videoCompressor.writingFile'));
      await ffmpeg.writeFile(inputName, await runtime.fetchFile(videoFile));

      setProgressText(t('imageProcessing.tools.videoCompressor.analyzing'));
      const probe = await probeInput(ffmpeg, inputName);
      const args = buildCompressionArgs(inputName, outputName, settings, targetDimensions, probe);
      const progressHandler: ProgressEventCallback = ({ progress: nextProgress }) => {
        const normalizedProgress = Number.isFinite(nextProgress) ? clamp(nextProgress, 0, 1) : 0;
        setProgress(Math.max(2, Math.round(normalizedProgress * 100)));
        if (normalizedProgress > 0) {
          const elapsed = (Date.now() - startedAtRef.current) / 1000;
          const total = elapsed / normalizedProgress;
          const remaining = Math.max(0, Math.ceil(total - elapsed));
          setProgressText(t('imageProcessing.tools.videoCompressor.remainingSeconds', { seconds: remaining }));
        }
      };

      ffmpeg.on('progress', progressHandler);
      try {
        await ffmpeg.exec(args);
      } finally {
        ffmpeg.off('progress', progressHandler);
      }

      setProgressText(t('imageProcessing.tools.videoCompressor.finalizing'));
      const data = await ffmpeg.readFile(outputName);
      const outputBlob = new Blob([data], { type: outputType });
      const outputUrl = URL.createObjectURL(outputBlob);
      const downloadName = `${getBaseFileName(videoFile.name)}-compressed.${settings.format}`;
      setResultBlob(outputBlob);
      setResultUrl(outputUrl);
      setProgress(100);
      setProgressText('');
      setStatus('completed');
      triggerBlobDownload(outputUrl, downloadName);
      message.success(t('imageProcessing.tools.videoCompressor.compressComplete'));
    } catch (error) {
      if (cancelRequestedRef.current) {
        setStatus('ready');
        setProgress(0);
        setProgressText('');
        message.info(t('imageProcessing.tools.videoCompressor.cancelled'));
      } else {
        console.error('Video compression failed:', error);
        setErrorText(error instanceof Error ? error.message : t('imageProcessing.tools.videoCompressor.compressFailed'));
        setStatus('error');
      }
    } finally {
      if (ffmpeg && ffmpeg.loaded) {
        await Promise.allSettled([
          ffmpeg.deleteFile(inputName),
          ffmpeg.deleteFile(outputName),
        ]);
      }
    }
  }, [clearResult, ensureFfmpeg, settings, t, targetDimensions, validateBeforeCompress, videoFile]);

  const cancelCompression = useCallback(() => {
    cancelRequestedRef.current = true;
    if (ffmpegRef.current) {
      ffmpegRef.current.terminate();
      ffmpegRef.current = null;
      fetchFileRef.current = null;
    }
    setStatus('ready');
    setProgress(0);
    setProgressText('');
  }, []);

  const resetTool = useCallback(() => {
    if (status === 'compressing' || status === 'loading') {
      cancelCompression();
    }
    setVideoFile(null);
    setPreviewUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return '';
    });
    setMetadata(null);
    setSettings(DEFAULT_SETTINGS);
    clearResult();
    setErrorText('');
    setStatus('idle');
  }, [cancelCompression, clearResult, status]);

  const downloadResult = useCallback(() => {
    if (!resultUrl || !videoFile) {
      return;
    }
    triggerBlobDownload(resultUrl, `${getBaseFileName(videoFile.name)}-compressed.${settings.format}`);
  }, [resultUrl, settings.format, videoFile]);

  const isBusy = status === 'loading' || status === 'compressing';

  return (
    <div className="video-compressor-page">
      <Card className="video-compressor-card">
        <ToolPageHeader
          backTo="/image-processing"
          backText={t('imageProcessing.tools.videoCompressor.backToImageProcessing')}
          title={t('imageProcessing.tools.videoCompressor.title')}
          icon={<CompressOutlined />}
          extra={<Tag color="cyan">{t('imageProcessing.tools.videoCompressor.localBadge')}</Tag>}
        />

        <Row gutter={[24, 24]} className="video-compressor-workbench">
          <Col xs={24} lg={13}>
            <Card className="video-panel" bordered={false}>
              <div className="panel-heading">
                <div>
                  <span className="panel-eyebrow">{t('imageProcessing.tools.videoCompressor.uploadEyebrow')}</span>
                  <Title level={3}>{t('imageProcessing.tools.videoCompressor.uploadTitle')}</Title>
                </div>
                {videoFile ? (
                  <Button icon={<ReloadOutlined />} onClick={resetTool} disabled={isBusy}>
                    {t('imageProcessing.tools.videoCompressor.changeVideo')}
                  </Button>
                ) : null}
              </div>

              {!videoFile ? (
                <Dragger
                  className="video-compressor-dropzone"
                  accept={SUPPORTED_EXTENSIONS.map((extension) => `.${extension}`).join(',')}
                  multiple={false}
                  showUploadList={false}
                  beforeUpload={(file) => {
                    handleFileSelect(file);
                    return false;
                  }}
                  disabled={isBusy}
                >
                  <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                  <p className="ant-upload-text">{t('imageProcessing.tools.videoCompressor.uploadHint')}</p>
                  <p className="ant-upload-hint">{t('imageProcessing.tools.videoCompressor.uploadSubHint')}</p>
                </Dragger>
              ) : (
                <div className="video-preview-card">
                  <video
                    src={previewUrl}
                    controls
                    className="video-preview"
                    onLoadedMetadata={handleMetadataLoaded}
                  />
                  <div className="video-file-meta">
                    <div>
                      <Text strong>{videoFile.name}</Text>
                      <div className="video-file-detail">
                        {formatFileSize(videoFile.size)} · {videoFile.type || t('imageProcessing.tools.videoCompressor.unknownType')}
                      </div>
                    </div>
                    <Space wrap>
                      <Tag icon={<VideoCameraOutlined />} color="blue">
                        {metadata?.width || '--'} × {metadata?.height || '--'}
                      </Tag>
                      <Tag icon={<PlayCircleOutlined />} color="geekblue">
                        {formatDuration(metadata?.duration || 0)}
                      </Tag>
                    </Space>
                  </div>
                </div>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={11}>
            <Card className="settings-panel" bordered={false}>
              <div className="panel-heading compact">
                <div>
                  <span className="panel-eyebrow">{t('imageProcessing.tools.videoCompressor.settingsEyebrow')}</span>
                  <Title level={3}>{t('imageProcessing.tools.videoCompressor.settingsTitle')}</Title>
                </div>
                <SettingOutlined className="settings-gear" />
              </div>

              <div className="setting-block">
                <Text strong>{t('imageProcessing.tools.videoCompressor.profile')}</Text>
                <Select<ProfilePreset>
                  value={settings.profile}
                  onChange={applyProfile}
                  disabled={isBusy}
                  className="full-width-control"
                  options={[
                    { value: 'fast', label: t('imageProcessing.tools.videoCompressor.profileFast') },
                    { value: 'balanced', label: t('imageProcessing.tools.videoCompressor.profileBalanced') },
                    { value: 'high', label: t('imageProcessing.tools.videoCompressor.profileHigh') },
                    { value: 'custom', label: t('imageProcessing.tools.videoCompressor.profileCustom') },
                  ]}
                />
              </div>

              <Row gutter={12}>
                <Col span={12}>
                  <div className="setting-block">
                    <Text strong>{t('imageProcessing.tools.videoCompressor.format')}</Text>
                    <Select<OutputFormat>
                      value={settings.format}
                      onChange={(format) => updateSettings({ format })}
                      disabled={isBusy}
                      className="full-width-control"
                      options={[
                        { value: 'mp4', label: t('imageProcessing.tools.videoCompressor.formatMp4') },
                        { value: 'webm', label: t('imageProcessing.tools.videoCompressor.formatWebm') },
                      ]}
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <div className="setting-block">
                    <Text strong>{t('imageProcessing.tools.videoCompressor.resolution')}</Text>
                    <Select<ResolutionHeight>
                      value={settings.resolutionHeight}
                      onChange={(resolutionHeight) => updateSettings({ resolutionHeight })}
                      disabled={isBusy}
                      className="full-width-control"
                      options={[
                        { value: 'source', label: t('imageProcessing.tools.videoCompressor.resolutionSource') },
                        { value: 1080, label: '1080p' },
                        { value: 720, label: '720p' },
                        { value: 480, label: '480p' },
                        { value: 360, label: '360p' },
                        { value: 'custom', label: t('imageProcessing.tools.videoCompressor.resolutionCustom') },
                      ]}
                    />
                  </div>
                </Col>
              </Row>

              {settings.resolutionHeight === 'custom' ? (
                <div className="setting-row">
                  <Text>{t('imageProcessing.tools.videoCompressor.customHeight')}</Text>
                  <InputNumber
                    min={120}
                    max={metadata?.height || 2160}
                    step={2}
                    value={settings.customHeight}
                    onChange={(value) => updateSettings({ customHeight: Number(value) || 720 })}
                    disabled={isBusy}
                  />
                </div>
              ) : null}

              <div className="setting-block">
                <div className="setting-label">
                  <Text strong>{t('imageProcessing.tools.videoCompressor.videoBitrate')}</Text>
                  <Text type="secondary">{settings.bitrateMbps.toFixed(2)} Mbps</Text>
                </div>
                <Slider
                  min={0.1}
                  max={12}
                  step={0.1}
                  value={settings.bitrateMbps}
                  onChange={(value) => updateSettings({ bitrateMbps: value })}
                  disabled={isBusy}
                />
              </div>

              <Row gutter={12}>
                <Col span={8}>
                  <div className="setting-block compact-control">
                    <Text strong>{t('imageProcessing.tools.videoCompressor.fps')}</Text>
                    <InputNumber
                      min={10}
                      max={60}
                      value={settings.fps}
                      onChange={(value) => updateSettings({ fps: Number(value) || 30 })}
                      disabled={isBusy}
                    />
                  </div>
                </Col>
                <Col span={8}>
                  <div className="setting-block compact-control">
                    <Text strong>{t('imageProcessing.tools.videoCompressor.crf')}</Text>
                    <InputNumber
                      min={18}
                      max={40}
                      value={settings.crf}
                      onChange={(value) => updateSettings({ crf: Number(value) || 26 })}
                      disabled={isBusy}
                    />
                  </div>
                </Col>
                <Col span={8}>
                  <div className="setting-block compact-control">
                    <Text strong>{t('imageProcessing.tools.videoCompressor.audioBitrate')}</Text>
                    <InputNumber
                      min={48}
                      max={320}
                      step={16}
                      value={settings.audioBitrateKbps}
                      onChange={(value) => updateSettings({ audioBitrateKbps: Number(value) || 128 })}
                      disabled={isBusy}
                    />
                  </div>
                </Col>
              </Row>

              <div className="compression-readout">
                <span>{targetDimensions.width} × {targetDimensions.height}</span>
                <span>{settings.fps} FPS</span>
                <span>{formatFileSize(estimatedOutputBytes)}</span>
              </div>

              {(status === 'loading' || status === 'compressing') ? (
                <div className="compress-progress">
                  <Progress percent={progress} status="active" />
                  <Text type="secondary">{progressText || t('imageProcessing.tools.videoCompressor.compressing')}</Text>
                </div>
              ) : null}

              {status === 'error' && errorText ? (
                <Alert type="error" showIcon className="compress-error" message={errorText} />
              ) : null}

              <Space direction="vertical" className="full-width-control">
                <Button
                  type="primary"
                  icon={<CompressOutlined />}
                  size="large"
                  block
                  onClick={startCompression}
                  loading={status === 'loading'}
                  disabled={!videoFile || status === 'compressing'}
                  className="compress-button"
                >
                  {status === 'completed'
                    ? t('imageProcessing.tools.videoCompressor.compressAgain')
                    : t('imageProcessing.tools.videoCompressor.compressButton')}
                </Button>
                {isBusy ? (
                  <Button danger icon={<StopOutlined />} block onClick={cancelCompression}>
                    {t('imageProcessing.tools.videoCompressor.cancel')}
                  </Button>
                ) : null}
              </Space>
            </Card>
          </Col>
        </Row>

        {status === 'completed' && resultBlob && resultUrl ? (
          <Card className="result-panel" bordered={false}>
            <div className="panel-heading compact">
              <div>
                <span className="panel-eyebrow">{t('imageProcessing.tools.videoCompressor.resultEyebrow')}</span>
                <Title level={3}>{t('imageProcessing.tools.videoCompressor.resultTitle')}</Title>
              </div>
              <Tag color={compressionRate >= 0 ? 'green' : 'orange'}>
                {compressionRate >= 0
                  ? t('imageProcessing.tools.videoCompressor.reducedBy', { percent: compressionRate })
                  : t('imageProcessing.tools.videoCompressor.outputLarger')}
              </Tag>
            </div>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Statistic title={t('imageProcessing.tools.videoCompressor.originalSize')} value={formatFileSize(videoFile?.size || 0)} />
              </Col>
              <Col xs={24} md={8}>
                <Statistic title={t('imageProcessing.tools.videoCompressor.outputSize')} value={formatFileSize(resultBlob.size)} />
              </Col>
              <Col xs={24} md={8}>
                <Statistic title={t('imageProcessing.tools.videoCompressor.elapsed')} value={`${elapsedSeconds}s`} />
              </Col>
            </Row>
            <Divider />
            <video src={resultUrl} controls className="result-video" />
            <Space wrap className="result-actions">
              <Button type="primary" icon={<CloudDownloadOutlined />} onClick={downloadResult}>
                {t('imageProcessing.tools.videoCompressor.download')}
              </Button>
              <Button icon={<ReloadOutlined />} onClick={startCompression}>
                {t('imageProcessing.tools.videoCompressor.compressAgain')}
              </Button>
              <Button onClick={resetTool}>{t('imageProcessing.tools.videoCompressor.compressNewVideo')}</Button>
            </Space>
          </Card>
        ) : null}
      </Card>
    </div>
  );
};

export default VideoCompressor;
